/**
 * TimeSelect - platform-styled time-of-day picker.
 *
 * Wraps the shared `Select` component with a fixed list of HH:MM
 * options covering the typical working day. This replaces native
 * `<input type="time">` so scheduling UIs share the same look +
 * interaction model as every other dropdown on the platform - no more
 * OS-specific time wheels.
 *
 * Value contract: HH:MM in 24-hour format ("09:30", "13:00"). The
 * display label is rendered as 12-hour AM/PM for readability.
 *
 * The `step` prop controls slot granularity in minutes (default 30).
 * Any divisor of 60 works (15, 20, 30, 60); the last hour never renders
 * a slot past ":00".
 */

import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/shared/components/ui/select'

const START_HOUR = 6
const END_HOUR = 20 // inclusive

function buildOptions(step: number): { value: string; label: string }[] {
  const out: { value: string; label: string }[] = []
  const safeStep = step > 0 && 60 % step === 0 ? step : 30
  for (let h = START_HOUR; h <= END_HOUR; h += 1) {
    for (let m = 0; m < 60; m += safeStep) {
      // Don't render a slot past the final hour (e.g. 20:15).
      if (h === END_HOUR && m > 0) break
      const value = `${String(h).padStart(2, '0')}:${String(m).padStart(2, '0')}`
      const hh12 = h === 0 ? 12 : h > 12 ? h - 12 : h
      const ampm = h >= 12 ? 'PM' : 'AM'
      const label = `${hh12}:${String(m).padStart(2, '0')} ${ampm}`
      out.push({ value, label })
    }
  }
  return out
}

export interface TimeSelectProps {
  /** HH:MM 24-hour string, or '' when nothing is picked. */
  value: string
  onChange: (value: string) => void
  placeholder?: string
  className?: string
  disabled?: boolean
  /** Slot granularity in minutes (divisor of 60). Defaults to 30. */
  step?: number
  /** Accessibility label when no visible <Label> is rendered. */
  ariaLabel?: string
}

export function TimeSelect({
  value,
  onChange,
  placeholder = 'Pick a time',
  className,
  disabled,
  step = 30,
  ariaLabel,
}: TimeSelectProps) {
  const options = buildOptions(step)
  return (
    <Select value={value} onValueChange={onChange} disabled={disabled}>
      <SelectTrigger
        className={className}
        aria-label={ariaLabel}
        data-testid="time-select-trigger"
      >
        <SelectValue placeholder={placeholder} />
      </SelectTrigger>
      <SelectContent>
        {options.map((o) => (
          <SelectItem key={o.value} value={o.value}>
            {o.label}
          </SelectItem>
        ))}
      </SelectContent>
    </Select>
  )
}
